/**
 * Repository Provider
 *
 * Provides repository instances to the React component tree via context.
 * Handles database initialization and loading states.
 * Enables dependency injection for easy testing and backend swapping.
 */

import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from 'react';
import { initializeDatabase } from '../db';
import { seedDatabase } from '../seed';
import {
  IndexedDBTicketRepository,
  IndexedDBTechRepository,
  IndexedDBUserRepository,
  IndexedDBArticleRepository,
  IndexedDBActivityRepository,
  IndexedDBNotificationRepository,
} from './indexeddb';
import type { ITicketRepository } from './interfaces/ITicketRepository';
import type { ITechRepository } from './interfaces/ITechRepository';
import type { IUserRepository } from './interfaces/IUserRepository';
import type { IArticleRepository } from './interfaces/IArticleRepository';
import type { IActivityRepository } from './interfaces/IActivityRepository';
import type { INotificationRepository } from './interfaces/INotificationRepository';

/**
 * Repository context value
 * Provides access to all repository implementations
 */
interface RepositoryContextValue {
  tickets: ITicketRepository;
  techs: ITechRepository;
  users: IUserRepository;
  articles: IArticleRepository;
  activities: IActivityRepository;
  notifications: INotificationRepository;
  isReady: boolean;
  error: Error | null;
}

const RepositoryContext = createContext<RepositoryContextValue | null>(null);

interface RepositoryProviderProps {
  children: ReactNode;
}

/**
 * Loading screen shown while database initializes
 */
function LoadingScreen() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-surface, #f5f5f5)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e0e0e0',
            borderTopColor: '#1976d2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }}
        />
        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

/**
 * Error screen shown when database initialization fails
 */
function ErrorScreen({ error }: { error: Error }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#fff5f5',
        padding: '20px',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <h2 style={{ color: '#c62828', marginBottom: '16px' }}>Database Error</h2>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Failed to initialize the database. Please try refreshing the page.
        </p>
        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#333',
            overflow: 'auto',
            textAlign: 'left',
          }}
        >
          {error.message}
        </pre>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

/**
 * Repository Provider Component
 *
 * Wraps the app to provide repository instances to all components.
 * Handles database initialization on mount.
 */
export function RepositoryProvider({ children }: RepositoryProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create repository instances once
  const repositories = useMemo(
    () => ({
      tickets: new IndexedDBTicketRepository(),
      techs: new IndexedDBTechRepository(),
      users: new IndexedDBUserRepository(),
      articles: new IndexedDBArticleRepository(),
      activities: new IndexedDBActivityRepository(),
      notifications: new IndexedDBNotificationRepository(),
    }),
    []
  );

  // Initialize database on mount
  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        await initializeDatabase(seedDatabase);
        if (mounted) {
          setIsReady(true);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Unknown database error'));
        }
      }
    }

    init();

    return () => {
      mounted = false;
    };
  }, []);

  // Context value with repositories and state
  const contextValue = useMemo<RepositoryContextValue>(
    () => ({
      ...repositories,
      isReady,
      error,
    }),
    [repositories, isReady, error]
  );

  // Show error screen if initialization failed
  if (error) {
    return <ErrorScreen error={error} />;
  }

  // Show loading screen while initializing
  if (!isReady) {
    return <LoadingScreen />;
  }

  return <RepositoryContext.Provider value={contextValue}>{children}</RepositoryContext.Provider>;
}

/**
 * Hook to access repository context
 * Throws if used outside RepositoryProvider
 */
export function useRepositoryContext(): RepositoryContextValue {
  const context = useContext(RepositoryContext);
  if (!context) {
    throw new Error('useRepositoryContext must be used within a RepositoryProvider');
  }
  return context;
}
